import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "sidebar:width";
const DEFAULT_WIDTH_PX = Math.round(16 * 16 * 0.65); // 65% of 16rem ≈ 166px
const MIN_WIDTH_PX = 140;
const MAX_WIDTH_PX = 400;

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_WIDTH_PX;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return DEFAULT_WIDTH_PX;
    return Math.min(MAX_WIDTH_PX, Math.max(MIN_WIDTH_PX, n));
  } catch {
    return DEFAULT_WIDTH_PX;
  }
}

export function useSidebarWidth() {
  const [width, setWidth] = useState<number>(() => readStoredWidth());
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragStart.current = { x: e.clientX, width };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMove = (ev: MouseEvent) => {
        const d = dragStart.current;
        if (!d) return;
        const next = Math.min(
          MAX_WIDTH_PX,
          Math.max(MIN_WIDTH_PX, d.width + ev.clientX - d.x),
        );
        setWidth(next);
      };

      const onUp = () => {
        dragStart.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        // Persist on release so mid-drag doesn't thrash localStorage.
        setWidth((w) => {
          try {
            localStorage.setItem(STORAGE_KEY, String(w));
          } catch {
            /* ignore */
          }
          return w;
        });
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [width],
  );

  // Cleanup any stray listeners if unmounted mid-drag.
  useEffect(() => {
    return () => {
      dragStart.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  return { width, startResize };
}
